import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useUserRole } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Menu, Search, LogOut, User, Settings, ClipboardPlus } from "lucide-react";
import { NotificationsPopover } from "./NotificationsPopover";
import { cn } from "@/lib/utils";
import { AssignTaskDialog } from "@/components/tasks/AssignTaskDialog";
import CashflowSignalBanner from "@/components/planning/overview/CashflowSignalBanner";

interface TopNavProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export function TopNav({ onMenuClick, showMenuButton }: TopNavProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [assignTaskOpen, setAssignTaskOpen] = useState(false);
  const { signOut } = useAuth();
  const { profile } = useProfile();
  const { isAdmin } = useUserRole();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const getInitials = (name: string | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className="sticky top-0 z-30 h-16 bg-white border-b border-[#E2E8F0]">
      <div className="flex items-center h-full px-4 md:px-6 gap-4">
        {/* Left — menu button (mobile) or breadcrumb area */}
        <div className="flex items-center gap-3 shrink-0">
          {showMenuButton && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onMenuClick}
              className="shrink-0 md:hidden -ml-1"
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">Open menu</span>
            </Button>
          )}
        </div>

        {/* Centre — search bar */}
        <div className="flex-1 flex justify-center">
          <div className={cn(
            "w-full max-w-[480px] transition-all",
            showMenuButton && !searchOpen && "hidden sm:block"
          )}>
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
              <Input
                type="search"
                placeholder="Search anything..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 rounded-full bg-[#F1F5F9] border-transparent hover:border-[#E2E8F0] focus:border-[#0BBBD5] focus-visible:ring-1 focus-visible:ring-[#0BBBD5] text-sm text-[#1A2B52] placeholder:text-[#94A3B8]"
                onBlur={() => setSearchOpen(false)}
              />
            </div>
          </div>
        </div>

        {/* Mobile search toggle */}
        {showMenuButton && !searchOpen && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSearchOpen(true)}
            className="shrink-0 sm:hidden"
          >
            <Search className="h-5 w-5" />
          </Button>
        )}

        {/* Right — actions */}
        <div className={cn(
          "flex items-center gap-1.5 shrink-0",
          searchOpen && "hidden sm:flex"
        )}>
          {/* Cashflow signal (AMBER/RED only) */}
          <CashflowSignalBanner />

          {/* Quick Assign Task (Admin only) */}
          {isAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setAssignTaskOpen(true)}
                  className="h-9 w-9 text-[#64748B] hover:text-[#1A2B52]"
                >
                  <ClipboardPlus className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Assign a task</TooltipContent>
            </Tooltip>
          )}

          {/* Notifications */}
          <NotificationsPopover />

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={profile?.avatar_url ?? undefined} />
                  <AvatarFallback className="bg-[#0BBBD5] text-white text-sm font-semibold">
                    {getInitials(profile?.full_name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="font-medium text-[#1A2B52]">{profile?.full_name ?? "User"}</span>
                  <span className="text-xs text-[#64748B] truncate">{profile?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings/profile")}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Assign Task Dialog */}
      {isAdmin && (
        <AssignTaskDialog open={assignTaskOpen} onOpenChange={setAssignTaskOpen} />
      )}
    </header>
  );
}
